import { BridgeClient } from 'app/service/bridge/BridgeClient';

/**
 * Creates a filters API backed by the bridge transport (postMessage → parent → plugin backend).
 *
 * Bridge actions used:
 *   facets.list        → BridgeUseCase → FacetCatalogService (discovery + overrides merge)
 *   facets.update_bulk → BridgeUseCase → FacetCustomizationService.updateBulk() + catalog re-read
 *
 * Bridge list response shape:
 *   { result, data: [{ id, title, type, visible, searchable, order_natural, order_value, sorting_mode, source }] }
 *
 * Admin facet model:
 *   { id, title, type, visible, searchable, orderNatural, order, sortingMode, source }
 *
 * Contract translation (bridge → admin):
 *   order_natural → orderNatural
 *   order_value   → order
 *   sorting_mode  → sortingMode
 *   visible       → visible  (bridge uses visible; cloud uses ignore inverted — adapter normalizes)
 *
 * Known bridge gaps vs cloud:
 *   COUNT/FREQUENCY: bridge does not provide count/frequency. They are omitted (not faked).
 *   IGNORE:          cloud uses `ignore` (boolean). Bridge uses `visible`. The adapter normalizes
 *                    to `visible` in the admin model. Cloud `ignore=true` maps to `visible=false`.
 */
export function createBridgeFiltersApi() {
  const client = new BridgeClient();

  /**
   * Map a single bridge facet record to the admin model.
   */
  function bridgeToAdmin(raw) {
    return {
      id:           raw.id,
      title:        raw.title,
      type:         raw.type || 'string',
      visible:      raw.visible !== undefined ? Boolean(raw.visible) : true,
      searchable:   raw.searchable !== undefined ? Boolean(raw.searchable) : true,
      orderNatural: raw.order_natural !== undefined ? Boolean(raw.order_natural) : true,
      order:        raw.order_value != null ? Number(raw.order_value) : null,
      sortingMode:  raw.sorting_mode || null,
      source:       raw.source || null,
    };
  }

  /**
   * Map an admin facet record to the bridge save payload shape.
   */
  function adminToBridge(facet) {
    return {
      id:            facet.id,
      title:         facet.title,
      type:          facet.type || 'string',
      visible:       Boolean(facet.visible),
      searchable:    Boolean(facet.searchable),
      order_natural: Boolean(facet.orderNatural),
      order_value:   facet.order != null ? Number(facet.order) : null,
      sorting_mode:  facet.sortingMode || null,
    };
  }

  return {
    /**
     * @param {string} _shopId — not used by bridge (context from plugin side), kept for interface parity
     * @returns {Promise<{ facets: Array }>}
     */
    async load(_shopId) {
      const response = await client.sendMessageWithResponse('facets.list', {});
      const rawFacets = response.data || [];
      const facets = rawFacets.map(bridgeToAdmin);
      return { facets };
    },

    /**
     * @param {string} _shopId
     * @param {Array} facets — admin model facets
     * @returns {Promise<{ facets: Array }>}
     */
    async save(_shopId, facets) {
      const bridgeFacets = facets.map(adminToBridge);
      const response = await client.sendMessageWithResponse('facets.update_bulk', {
        facets: bridgeFacets,
      });
      // update_bulk now returns the full catalog — map it to admin model
      const rawFacets = response.data || [];
      return { facets: rawFacets.map(bridgeToAdmin) };
    },
  };
}
